import { compact, uniq } from 'lodash'
import {
  currentPageParams,
  DataItem,
  provideComponent,
  useDataScope,
} from 'scrivito'

import { DXPMyActivitiesWidget } from './DXPMyActivitiesTableWidgetClass'
import { Column } from '@/Atomic/Templates/Table'
import { DataErrorEditorNote } from '@/Components/DataErrorEditorNote'
import { DisplayCountIndicator } from '@/Components/DisplayCountIndicator'
import { EditorNote } from '@/Components/EditorNote'
import { EntityListLoadingIndicator } from '@/Components/EntityListLoadingIndicator'
import { EntityListNoResultsIndicator } from '@/Components/EntityListNoResultsIndicator'
import { EntitySearchHeader } from '@/Components/EntitySearchHeader/EntitySearchHeader'
import { EntityTable } from '@/Components/EntityTable/EntityTable'
import { DATA_SCOPE_ERROR, ErrorVariant } from '@/Components/error/constants'
import { ErrorPage } from '@/Components/error/Errors'
import { Loading } from '@/Components/Loading'
import { ActivityCompaniesCell } from '@/Components/TableCells/ActivityCompaniesCell'
import { ActivityOverviewCell } from '@/Components/TableCells/ActivityOverviewCell'
import { ActivityPersonsCell } from '@/Components/TableCells/ActivityPersonsCell'
import { ActivityTimelineCell } from '@/Components/TableCells/ActivityTimelineCell'
import { Activity } from '@/Data/Activity/ActivityDataClass'
import { DataClassName } from '@/Data/const'
import { SchemaAttributes } from '@/Data/types'
import { WhoAmI } from '@/Data/WhoAmI/WhoAmIDataItem'
import { getSchemaName } from '@/utils/dataClassMap'
import { ensureString } from '@/utils/ensureString'
import { paramsToFilters } from '@/utils/filterParams'
import { getDataItem } from '@/utils/getDataItem'
import { getDataItems } from '@/utils/getDataItems'
import { getSchema } from '@/utils/getSchema'
import { getTableColumns } from '@/utils/getTableColumns'
import { getLocalizedSchemaAttributes } from '@/utils/schemaLocalization'

provideComponent(
  DXPMyActivitiesWidget,
  () => {
    const relationDataScope = useDataScope()
    const contactId = WhoAmI.get('contactId') as string | null

    const params = currentPageParams()
    const search = ensureString(params['search'] ?? null)
    const filter = ensureString(params['filter'] ?? null)
    const query = ensureString(params['query'] ?? null)
    const labels = ensureString(params['labels'] ?? null)
    const attributeFilters = paramsToFilters(params)
    // Remount the loading/no-results indicators whenever the query changes, so
    // Scrivito re-fires their { loading } fallback (it only shows on a fresh
    // mount). Keyed on the full params — stable across load-more (limit is
    // provider state, not a URL param), so the table itself never flashes.
    const queryKey = JSON.stringify(params)

    const { dataItems: activityContactRelations, error: relationsDataError } =
      contactId
        ? getDataItems(relationDataScope)
        : { dataItems: [] as DataItem[] }

    const activityIds = uniq(
      activityContactRelations.map((item) => item.get('activity') as string),
    )

    let activitiesDataError: Error | undefined
    const activities = compact(
      activityIds.map((id) => {
        const { dataItem, error } = getDataItem(Activity, id)
        activitiesDataError = activitiesDataError || error
        return dataItem
      }),
    )

    const { schema, error: schemaError } = getSchema(
      getSchemaName(DataClassName.ACTIVITY),
    )

    const schemaAttributes = getLocalizedSchemaAttributes(
      schema?.get('attributes') as SchemaAttributes,
    )

    const tableColumns = getTableColumns(
      ACTIVITIES_TABLE_COLS,
      schemaAttributes,
    )

    if (!contactId) {
      return <Loading />
    }

    if (relationsDataError === DATA_SCOPE_ERROR) {
      return <EditorNote>No Data Scope is defined</EditorNote>
    }

    if (relationsDataError) {
      return <DataErrorEditorNote error={relationsDataError} />
    }

    if (activitiesDataError) {
      return <DataErrorEditorNote error={activitiesDataError} />
    }

    if (!schema || schemaError) {
      return <ErrorPage variant={ErrorVariant.InternalServerError} />
    }

    return (
      <>
        <EntitySearchHeader
          dataClassName={DataClassName.ACTIVITY_CONTACT_RELATION}
          showDateRangeFilter={true}
          dateRangeFilterConfig={{ fieldPrefix: 'activity' }}
        />

        <div className="row">
          <div className="col-sm-12">
            <DisplayCountIndicator dataClassName={DataClassName.ACTIVITY} />
            {(search ||
              filter ||
              query ||
              labels ||
              Object.keys(attributeFilters).length > 0) && (
              <>
                <EntityListLoadingIndicator key={`loading-${queryKey}`} />
                <EntityListNoResultsIndicator key={`no-results-${queryKey}`} />
              </>
            )}
            <EntityTable
              tableColumns={tableColumns}
              dataItems={activities}
              schema={schema}
              dataClassName={DataClassName.ACTIVITY_CONTACT_RELATION}
            />
          </div>
        </div>
      </>
    )
  },
  { loading: Loading },
)

export const ACTIVITIES_TABLE_COLS = [
  {
    accessor: 'subject',
    title: 'Activity',
    titleKey: 'activity',
    main: true,
    variant: Column.MAIN,
    noWrap: true,
    cell: ActivityOverviewCell,
  },
  {
    accessor: 'status',
    title: 'Status',
    variant: Column.WIDTH_160PX,
    noWrap: true,
  },
  {
    accessor: 'priority',
    title: 'Priority',
    variant: Column.WIDTH_80PX,
  },
  {
    accessor: 'beginAt',
    title: 'Timeline',
    titleKey: 'timeline',
    variant: Column.WIDTH_160PX,
    cell: ActivityTimelineCell,
  },
  {
    accessor: 'companies',
    title: 'Companies',
    titleKey: 'companies',
    variant: Column.FLEX,
    cell: ActivityCompaniesCell,
  },
  {
    accessor: 'persons',
    title: 'Persons',
    titleKey: 'persons',
    variant: Column.FLEX,
    cell: ActivityPersonsCell,
  },
  {
    accessor: '',
    title: '',
    variant: Column.ACTIONS,
  },
]
